import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { format, startOfDay, endOfDay, subDays, differenceInCalendarDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getAppointmentClient } from '@/lib/api-clients';
import { downloadBlob } from '@/lib/download';

/** Max export span (inclusive), in days — mirrors the API cap. */
const MAX_RANGE_DAYS = 60;

/**
 * Export scheduled appointments in a chosen date range to an XLSX file.
 *
 * The backend generates the spreadsheet (PHI export stays server-side); this
 * component just picks the range, fetches the blob, and triggers a download.
 * Lives on the Referrals page header as a `headerAction`.
 */
export function ExportAppointmentsButton(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(() => ({
    from: startOfDay(subDays(new Date(), 30)),
    to: endOfDay(new Date()),
  }));

  const exportMutation = useMutation({
    mutationFn: async (selected: { from: Date; to: Date }): Promise<void> => {
      const from = format(selected.from, 'yyyy-MM-dd');
      const to = format(selected.to, 'yyyy-MM-dd');
      const blob = await getAppointmentClient().exportSchedule({ from, to });
      downloadBlob(blob, `scheduled-appointments_${from}_${to}.xlsx`);
    },
    onSuccess: () => {
      setOpen(false);
      toast.success('Export downloaded');
    },
  });

  const handleExport = (): void => {
    if (!range?.from || !range?.to) {
      toast.error('Select a start and end date');
      return;
    }
    if (differenceInCalendarDays(range.to, range.from) + 1 > MAX_RANGE_DAYS) {
      toast.error(`Select a range of ${MAX_RANGE_DAYS} days or fewer`);
      return;
    }
    exportMutation.mutate({ from: range.from, to: range.to });
  };

  const label =
    range?.from && range?.to
      ? `${format(range.from, 'MMM d')} – ${format(range.to, 'MMM d, yyyy')}`
      : 'Select dates';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="mr-1 h-4 w-4" />
          Export appointments
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex flex-col">
          <div className="border-b px-3 py-2 text-sm font-medium">{label}</div>
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            showOutsideDays={false}
            defaultMonth={range?.from}
          />
          <div className="flex justify-end border-t p-2">
            <Button size="sm" onClick={handleExport} disabled={exportMutation.isPending}>
              {exportMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1 h-4 w-4" />
              )}
              Download XLSX
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
